/**
 * Os **nomes de cabecalho** do protocolo, como constantes.
 *
 * Fonte: `API.md §1.2` (tabela de cabecalhos), `§3` (idempotencia), `§4` (concorrencia
 * otimista) e `§9` (rate limit).
 *
 * Por que isto mora em `contracts` e nao no cliente HTTP: cabecalho e **contrato**, do mesmo
 * jeito que o corpo. `apps/api` ja tinha `IDEMPOTENCY_HEADER = 'idempotency-key'` escrito em
 * `platform/idempotency/`, e `@farmacia/api-client` precisaria da mesma string — duas
 * transcricoes da mesma linha da tabela, que e exatamente a duplicacao que `API §10.3` proibe.
 * O `CSRF_HEADER_NAME` ja vivia aqui (em `public/auth.ts`, junto do modelo de sessao) pelo
 * mesmo motivo; este arquivo completa a lista.
 *
 * **Tudo em minusculo.** `Headers` do fetch e `request.headers` do Fastify normalizam para
 * minusculo; escrever `'Idempotency-Key'` aqui faria a comparacao com o que chega falhar em
 * metade dos lugares e passar na outra metade.
 */

// ---------------------------------------------------------------------------
// Correlacao e identificacao do cliente — API §1.2
// ---------------------------------------------------------------------------

/** `X-Request-Id`. UUID; se ausente, o servidor gera. Ecoado em toda resposta e todo log. */
export const REQUEST_ID_HEADER_NAME = 'x-request-id' as const;

/**
 * `X-Client`. **Obrigatorio** (`API §1.2`): `web-cliente/1.4.2`, `web-interno/1.4.2`,
 * `sync-agent/0.9.0`. Dirige feature flag e telemetria.
 */
export const CLIENT_HEADER_NAME = 'x-client' as const;

/** Os tres valores de `X-Client` que a tabela do `API §1.2` nomeia. */
export const CLIENT_IDS = ['web-cliente', 'web-interno', 'sync-agent'] as const;
export type ClientId = (typeof CLIENT_IDS)[number];

/** `Accept-Language`. So `pt-BR` e suportado; outro valor e ignorado, nao e erro. */
export const ACCEPT_LANGUAGE_HEADER_NAME = 'accept-language' as const;
export const API_LANGUAGE = 'pt-BR' as const;

// ---------------------------------------------------------------------------
// Idempotencia — API §3
// ---------------------------------------------------------------------------

/** `Idempotency-Key`, nas rotas de escrita nao-idempotente da tabela do `API §3.1`. */
export const IDEMPOTENCY_KEY_HEADER_NAME = 'idempotency-key' as const;

/** `Idempotent-Replayed: true` acompanha a resposta gravada devolvida no replay (`API §3.2`). */
export const IDEMPOTENT_REPLAYED_HEADER_NAME = 'idempotent-replayed' as const;

/** `Idempotent-Original-Request-Id` — o `X-Request-Id` da requisicao que de fato executou. */
export const IDEMPOTENT_ORIGINAL_REQUEST_ID_HEADER_NAME =
  'idempotent-original-request-id' as const;

// ---------------------------------------------------------------------------
// Concorrencia otimista — API §4
// ---------------------------------------------------------------------------

export const ETAG_HEADER_NAME = 'etag' as const;
export const IF_MATCH_HEADER_NAME = 'if-match' as const;
export const IF_NONE_MATCH_HEADER_NAME = 'if-none-match' as const;

// ---------------------------------------------------------------------------
// Rate limit — API §9
// ---------------------------------------------------------------------------

export const RETRY_AFTER_HEADER_NAME = 'retry-after' as const;

/** `RateLimit: limit=60, remaining=0, reset=42` (`API §9`). */
export const RATE_LIMIT_HEADER_NAME = 'ratelimit' as const;

// ---------------------------------------------------------------------------
// Tipos de midia
// ---------------------------------------------------------------------------

/** RFC 9457. E por este `Content-Type` que uma resposta de erro se identifica (`API §2.1`). */
export const PROBLEM_JSON_MEDIA_TYPE = 'application/problem+json' as const;

export const JSON_MEDIA_TYPE = 'application/json' as const;

// ---------------------------------------------------------------------------
// A forma do `ETag` fraco — CONTRACTS §7.2, API §4.2
// ---------------------------------------------------------------------------

/**
 * `W/"cart-<id>-<version>"` e `W/"order-<id>-<version>"`.
 *
 * A forma mora aqui, e nao no servidor, porque **os dois lados precisam produzi-la**. O
 * servidor a emite no header; o cliente precisa REMONTA-LA quando o `409 CART_VERSION_CONFLICT`
 * traz o carrinho atual em `meta.current` sem um `ETag` de header junto (`API §4.2`: o corpo do
 * conflito existe justamente para poupar um segundo `GET`). Duas implementacoes da mesma
 * gramatica dariam um `If-Match` que nunca casa — e o sintoma seria conflito eterno no
 * carrinho, nao um erro de compilacao.
 *
 * `W/` (fraco) porque a serializacao pode variar por `fields` sem que o recurso tenha mudado, e
 * a versao e um inteiro monotonico da linha, nao um hash do corpo (`API §4.2`).
 */
export function weakETag(prefix: 'cart' | 'order', id: string, version: number): string {
  return `W/"${prefix}-${id}-${String(version)}"`;
}

export function cartWeakETag(cartId: string, version: number): string {
  return weakETag('cart', cartId, version);
}

export function orderWeakETag(orderId: string, version: number): string {
  return weakETag('order', orderId, version);
}
